import {LocalDate} from 'js-joda'
import {
    Decoder,
    equal,
    field,
    number,
    object,
    oneOf,
    string
} from 'json-bouncer'
import * as R from 'ramda'
import {localDateDecoder} from '../../../Data/DecodeHelper'
import * as Nullable from '../../../Data/Nullable'
import {BpsUser, bpsUserDecoder} from '../../../Entities/BpsUser'
import {Company, companyDecoder} from '../../../Entities/Company'
import {Customer, customerDecoder} from '../../../Entities/Customer'
import {
    OrderPaid,
    orderPaidDecoder,
    orderPaidLabel
} from '../../../Entities/OrderPaid'
import * as OrderStatus from '../../../Entities/OrderStatus'
import {
    ShippingMethod,
    shippingMethodDecoder
} from '../../../Entities/ShippingMethod'
import {OrderFilterInput} from './OrderFilterInput'
import * as OrderFilterType from './OrderFilterType'

export type OrderFilter =
    | StatusFilter
    | CustomerFilter
    | BpsUserFilter
    | CompanyFilter
    | OrderNumberFilter
    | ShippingMethodFilter
    | CreatedFromFilter
    | CreatedToFilter
    | PriceFromFilter
    | PriceToFilter
    | OrderPaidFilter

export interface StatusFilter {
    readonly type: 'StatusFilter'
    readonly status: OrderStatus.M
}

export interface CustomerFilter {
    readonly type: 'CustomerFilter'
    readonly customer: Customer
}

export interface BpsUserFilter {
    readonly type: 'BpsUserFilter'
    readonly bpsUser: BpsUser
}

export interface CompanyFilter {
    readonly type: 'CompanyFilter'
    readonly company: Company
}

export interface OrderNumberFilter {
    readonly type: 'OrderNumberFilter'
    readonly orderNumber: string
}

export interface ShippingMethodFilter {
    readonly type: 'ShippingMethodFilter'
    readonly shippingMethod: ShippingMethod
}

export interface CreatedFromFilter {
    readonly type: 'CreatedFromFilter'
    readonly createdFrom: LocalDate
}

export interface CreatedToFilter {
    readonly type: 'CreatedToFilter'
    readonly createdTo: LocalDate
}

export interface PriceFromFilter {
    readonly type: 'PriceFromFilter'
    readonly priceFrom: number
}

export interface PriceToFilter {
    readonly type: 'PriceToFilter'
    readonly priceTo: number
}

export interface OrderPaidFilter {
    readonly type: 'OrderPaidFilter'
    readonly orderPaid: OrderPaid
}

export function decoder(): Decoder<OrderFilter> {
    return oneOf<OrderFilter>([
        object({
            type: field('type', equal<'StatusFilter'>('StatusFilter')),
            status: field('status', OrderStatus.decoder()),
        }),
        object({
            type: field('type', equal<'CustomerFilter'>('CustomerFilter')),
            customer: field('customer', customerDecoder()),
        }),
        object({
            type: field('type', equal<'BpsUserFilter'>('BpsUserFilter')),
            bpsUser: field('bpsUser', bpsUserDecoder()),
        }),
        object({
            type: field('type', equal<'CompanyFilter'>('CompanyFilter')),
            company: field('company', companyDecoder()),
        }),
        object({
            type: field('type', equal<'OrderNumberFilter'>('OrderNumberFilter')),
            orderNumber: field('orderNumber', string()),
        }),
        object({
            type: field('type', equal<'ShippingMethodFilter'>('ShippingMethodFilter')),
            shippingMethod: field('shippingMethod', shippingMethodDecoder()),
        }),
        object({
            type: field('type', equal<'CreatedFromFilter'>('CreatedFromFilter')),
            createdFrom: field('createdFrom', localDateDecoder()),
        }),
        object({
            type: field('type', equal<'CreatedToFilter'>('CreatedToFilter')),
            createdTo: field('createdTo', localDateDecoder()),
        }),
        object({
            type: field('type', equal<'PriceFromFilter'>('PriceFromFilter')),
            priceFrom: field('priceFrom', number()),
        }),
        object({
            type: field('type', equal<'PriceToFilter'>('PriceToFilter')),
            priceTo: field('priceTo', number()),
        }),
        object({
            type: field('type', equal<'OrderPaidFilter'>('OrderPaidFilter')),
            orderPaid: field('orderPaid', orderPaidDecoder()),
        }),
    ])
}

export function toFilterType(
    filter: OrderFilter,
): OrderFilterType.OrderFilterType {
    switch (filter.type) {
        case 'StatusFilter':
            return 'Status'
        case 'CustomerFilter':
            return 'Customer'
        case 'BpsUserFilter':
            return 'BpsUser'
        case 'CompanyFilter':
            return 'Company'
        case 'OrderNumberFilter':
            return 'OrderNumber'
        case 'ShippingMethodFilter':
            return 'ShippingMethod'
        case 'CreatedFromFilter':
            return 'CreatedFrom'
        case 'CreatedToFilter':
            return 'CreatedTo'
        case 'PriceFromFilter':
            return 'PriceFrom'
        case 'PriceToFilter':
            return 'PriceTo'
        case 'OrderPaidFilter':
            return 'OrderPaid'
    }
}

export function label(filter: OrderFilter): string {
    switch (filter.type) {
        case 'StatusFilter':
            return OrderStatus.label(filter.status)
        case 'CustomerFilter':
            return filter.customer.longName
        case 'BpsUserFilter':
            return `${filter.bpsUser.lastName}, ${filter.bpsUser.firstName}`
        case 'CompanyFilter':
            return filter.company.name1
        case 'OrderNumberFilter':
            return `Auftragsnummer ${filter.orderNumber}`
        case 'ShippingMethodFilter':
            return `Versand ${filter.shippingMethod}`
        case 'CreatedFromFilter':
            return `Datum von ${filter.createdFrom}`
        case 'CreatedToFilter':
            return `Datum bis ${filter.createdTo}`
        case 'PriceFromFilter':
            return `Preis ab ${filter.priceFrom.toFixed(2)} €`
        case 'PriceToFilter':
            return `Preis bis ${filter.priceTo.toFixed(2)} €`
        case 'OrderPaidFilter':
            return `Bezahlstatus ${orderPaidLabel(filter.orderPaid)}`
    }
}

export function inactiveFilterTypes(
    filters: OrderFilter[],
): OrderFilterType.OrderFilterType[] {
    return R.difference(
        OrderFilterType.all(),
        R.map(toFilterType, filters)
    )
}

export function fromFilterInput(
    filterInput: OrderFilterInput,
): OrderFilter | null {
    switch (filterInput.type) {
        case 'StatusFilterInput': {
            return Nullable.map<OrderStatus.M, OrderFilter>(
                status => ({type: 'StatusFilter', status}),
                filterInput.value,
            )
        }
        case 'CustomerFilterInput': {
            const value = filterInput.value
            if (filterInput.customers.type === 'Success' && value !== null) {
                const selectedCustomer =
                    filterInput.customers.value.find(
                        customer => customer.id === value)
                if (typeof selectedCustomer !== 'undefined') {
                    return {
                        type: 'CustomerFilter',
                        customer: selectedCustomer,
                    }
                } else {
                    return null
                }
            } else {
                return null
            }
        }
        case 'BpsUserFilterInput': {
            return Nullable.map<BpsUser, OrderFilter>(
                bpsUser => ({type: 'BpsUserFilter', bpsUser}),
                filterInput.selectedBpsUser
            )
        }
        case 'CompanyFilterInput': {
            return Nullable.map<Company, OrderFilter>(
                company => ({type: 'CompanyFilter', company}),
                filterInput.selectedCompany,
            )
        }
        case 'OrderNumberFilterInput': {
            return Nullable.map<string, OrderFilter>(
                orderNumber => ({type: 'OrderNumberFilter', orderNumber}),
                filterInput.value,
            )
        }
        case 'ShippingMethodFilterInput': {
            return Nullable.map<ShippingMethod, OrderFilter>(
                shippingMethod =>
                    ({type: 'ShippingMethodFilter', shippingMethod}),
                filterInput.value,
            )
        }
        case 'DateFromFilterInput': {
            return Nullable.map<LocalDate, OrderFilter>(
                createdFrom => ({type: 'CreatedFromFilter', createdFrom}),
                filterInput.value,
            )
        }
        case 'DateToFilterInput': {
            return Nullable.map<LocalDate, OrderFilter>(
                createdTo => ({type: 'CreatedToFilter', createdTo}),
                filterInput.value,
            )
        }
        case 'PriceFromFilterInput': {
            return Nullable.map<number, OrderFilter>(
                priceFrom => ({type: 'PriceFromFilter', priceFrom}),
                parseFloat(filterInput.value),
            )
        }
        case 'PriceToFilterInput': {
            return Nullable.map<number, OrderFilter>(
                priceTo => ({type: 'PriceToFilter', priceTo}),
                parseFloat(filterInput.value),
            )
        }
        case 'OrderPaidFilterInput': {
            return Nullable.map<OrderPaid, OrderFilter>(
                orderPaid => ({type: 'OrderPaidFilter', orderPaid}),
                filterInput.value,
            )
        }
    }
}



// WEBPACK FOOTER //
// ./node_modules/tslint-loader!./src/Components/OrderFiltering/Model/OrderFilter.ts